// A minimal focus trap for the capture sheet (REQ-CAPTURE-008). Pure DOM, no framework —
// so it runs in jsdom under the interaction test tier. Keeps Tab / Shift+Tab within the
// container while active; on release, returns focus to whatever was focused when it
// activated (closing the sheet returns focus to ＋ Capture).
export interface FocusTrap {
  activate(): void;
  release(): void;
}

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function createFocusTrap(container: HTMLElement, doc: Document = document): FocusTrap {
  let previouslyFocused: HTMLElement | null = null;

  const focusables = (): HTMLElement[] =>
    Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));

  function onKeydown(e: KeyboardEvent): void {
    if (e.key !== "Tab") return;
    const els = focusables();
    if (els.length === 0) {
      e.preventDefault();
      return;
    }
    const first = els[0];
    const last = els[els.length - 1];
    const active = doc.activeElement as HTMLElement | null;
    const inside = active ? container.contains(active) : false;
    if (e.shiftKey) {
      if (!inside || active === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  return {
    activate(): void {
      previouslyFocused = doc.activeElement as HTMLElement | null;
      container.addEventListener("keydown", onKeydown as EventListener);
      const els = focusables();
      (els[0] ?? container).focus();
    },
    release(): void {
      container.removeEventListener("keydown", onKeydown as EventListener);
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    },
  };
}
